import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Stack,
  useTheme,
  alpha,
  IconButton,
  Skeleton,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../core/hooks/useStore";
import { RootState } from "../../../core/store/store";
import toolsSlice, { KeywordJobData, ToolJobData } from "./keyword-pig.slice";
import useTool from "./use-keyword-pig";

const getTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  let interval = Math.floor(seconds / 31536000);
  if (interval >= 1) {
    return interval === 1 ? "1 year ago" : `${interval} years ago`;
  }
  
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) {
    return interval === 1 ? "1 month ago" : `${interval} months ago`;
  }
  
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) {
    return interval === 1 ? "1 day ago" : `${interval} days ago`;
  }
  
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) {
    return interval === 1 ? "1 hour ago" : `${interval} hours ago`;
  }
  
  interval = Math.floor(seconds / 60);
  if (interval >= 1) {
    return interval === 1 ? "1 minute ago" : `${interval} minutes ago`;
  }
  
  return "Just now";
};

const KeywordPigRecentSearches = (props: { selected?: string }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [selection, setSelection] = useState<string>("");
  const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [jobs, setJobs] = useState<KeywordJobData[]>([]);
  const toolsState = useSelector((state: RootState) => state.keywordPig);
  const tool = useTool();

  useEffect(() => {
    const getJobs = async () => {
      const jobs = await tool.listJobs("KEYWORD");
      if (jobs) {
        setJobs(jobs as KeywordJobData[]);
      } else {
        setJobs([]);
      }
    };
    getJobs();
  }, [toolsState.tool.status]);

  const exportOpen = Boolean(exportAnchorEl);

  const handleChange = (event: SelectChangeEvent<string>) => {
    setSelection(event.target.value);
  };

  const handleSearchOpen = (job: ToolJobData) => {
    navigate(`/tools/keyword-tool/${job._id}`);
  };

  const handleBackButton = () => {
    navigate("/tools/keyword-tool");
  };

  return (
    <FormControl
      sx={{
        px: 4,
        minWidth: 120,
        maxWidth: "300px",
        width: "100%",
        fontSize: "1.25rem",
      }}
    >
      <Select
        value={props.selected ? props.selected : "-1"}
        label="Recent Searches"
        placeholder="Recent Searches"
        onChange={handleChange}
        MenuProps={{
          PaperProps: {
            style: {},
          },
        }}
      >
        <MenuItem key={-1} value={"-1"} onClick={handleBackButton}>
          None Selected
        </MenuItem>
        {[...jobs]
          .filter((job) => job.data.niche)
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .map((job) => (
            <MenuItem
              key={job._id}
              value={job._id}
              onClick={() => {
                handleSearchOpen(job);
              }}
            >
              {job.data.niche}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};

export default KeywordPigRecentSearches;

export const KeywordPigRecentSearchesCards = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<ToolJobData[]>([]);
  const [loading, setLoading] = useState(true);
  const toolsState = useSelector((state: RootState) => state.keywordPig);
  const tool = useTool();

  useEffect(() => {
    const getJobs = async () => {
      setLoading(true);
      const jobs = await tool.listJobs("KEYWORD");
      if (jobs) {
        setJobs(jobs);
      } else {
        setJobs([]);
      }
      setLoading(false);
    };
    getJobs();
  }, [toolsState.tool.status]);

  const handleSearchOpen = (job: ToolJobData) => {
    navigate(`/tools/keyword-tool/${job._id}`);
  };

  const recentJobs = [...jobs]
    .filter((job) => (job.data as any).niche)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  if (loading) {
    return (
      <Box sx={{ mt: 6, mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <AccessTimeIcon sx={{ color: theme.palette.text.secondary }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Recent Searches
          </Typography>
        </Stack>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (recentJobs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <AccessTimeIcon sx={{ color: theme.palette.text.secondary }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Recent Searches
        </Typography>
      </Stack>
      
      <Grid container spacing={3}>
        {recentJobs.slice(0, 6).map((job) => (
          <Grid item xs={12} sm={6} md={4} key={job._id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s',
                height: '100%',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[4],
                  borderColor: theme.palette.primary.main,
                  '& .open-icon': {
                    opacity: 1
                  }
                }
              }}
              onClick={() => handleSearchOpen(job)}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600, 
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {(job.data as any).niche}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                      <Typography variant="caption" color="text.secondary">
                        {getTimeAgo(new Date(job.createdAt))}
                      </Typography>
                    </Stack>
                    <Chip
                      label={`${(job.data as any).keywords?.length || 0} keywords`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>
                  <IconButton
                    className="open-icon"
                    size="small"
                    sx={{
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      color: theme.palette.primary.main,
                      mt: -0.5
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
